import { useSession } from 'next-auth/react';
import { useState } from 'react';
import { canImpersonate, type ImpersonationType } from '@/lib/impersonation';

export function useImpersonation() {
  const { data: session, update } = useSession();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startImpersonation = async (userId: string, type: 'user' | 'tenant_admin') => {
    try {
      setLoading(true);
      setError(null);

      const endpoint = session?.user?.isSuperAdmin 
        ? '/api/superadmin/impersonate' 
        : '/api/admin/impersonate';

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, type }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to start impersonation');
      }

      const { impersonationGrant } = await response.json();

      await update({
        impersonationGrant,
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start impersonation';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const stopImpersonation = async () => {
    try {
      setLoading(true);
      setError(null);

      // Update the session to stop impersonation
      await update({
        stopImpersonation: true,
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to stop impersonation';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const canImpersonateUser = (targetUser: {
    id: string;
    tenantId?: string | null;
    isAdmin: boolean;
    isSuperAdmin: boolean;
  }) => {
    if (!session?.user) return false;

    const impersonationType = getImpersonationType(targetUser);

    return canImpersonate(
      {
        id: session.user.id,
        isAdmin: session.user.isAdmin,
        isSuperAdmin: session.user.isSuperAdmin,
        tenantId: session.user.tenantId,
      },
      {
        id: targetUser.id,
        isAdmin: targetUser.isAdmin,
        isSuperAdmin: targetUser.isSuperAdmin,
        tenantId: targetUser.tenantId ?? null,
      },
      impersonationType
    );
  };

  const getImpersonationType = (targetUser: {
    isAdmin: boolean;
  }): ImpersonationType => {
    if (session?.user?.isSuperAdmin && targetUser.isAdmin) {
      return 'tenant_admin';
    }
    return 'user';
  };

  return {
    startImpersonation,
    stopImpersonation,
    canImpersonate: canImpersonateUser,
    getImpersonationType,
    loading,
    error,
    isImpersonating: session?.user?.isImpersonating || false,
    originalUser: session?.user?.isImpersonating ? {
      id: session.user.originalUserId,
      email: session.user.originalUserEmail,
      name: session.user.originalUserName,
    } : null,
  };
} 